import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { useClinicTheme } from "../../theme";
import { COLORS, TYPO } from "../../styles/uiConstants";

// Figür kutusu — noktalar bu kutuya göre 0–1 arası normalize saklanır
// ({ view, x, y, label }); kutu ölçüsü değişse de konum korunur.
const VB_W = 200;
const VB_H = 430;

export const BODY_VIEWS = [
  { value: "front", label: "Ön" },
  { value: "back", label: "Arka" },
];
export const VIEW_LABEL = { front: "Ön", back: "Arka" };

// Basit insan silueti (sol kol + sol bacak çizilir, sağ taraf aynalanır).
const HEAD = { cx: 100, cy: 36, r: 24 };
const NECK = "M90 58 h20 v16 h-20 z";
const TORSO = "M58 78 Q100 66 142 78 Q150 140 146 200 Q100 214 54 200 Q50 140 58 78 Z";
const ARM = "M58 80 Q42 86 38 128 L30 228 Q33 240 43 237 L52 160 Q58 124 64 112 Z";
const LEG = "M56 198 Q78 210 98 208 L95 300 L90 410 Q80 420 68 413 L66 300 Z";

function Silhouette({ view, fill, stroke }) {
  const common = { fill, stroke, strokeWidth: 1.5, strokeLinejoin: "round" };
  return (
    <g>
      <circle {...HEAD} {...common} />
      <path d={NECK} {...common} />
      <g>
        <path d={ARM} {...common} />
        <path d={LEG} {...common} />
      </g>
      <g transform={`translate(${VB_W},0) scale(-1,1)`}>
        <path d={ARM} {...common} />
        <path d={LEG} {...common} />
      </g>
      <path d={TORSO} {...common} />
      {view === "back" ? (
        // Arka: omurga çizgisi + kürek kemiği izleri
        <g stroke={stroke} strokeWidth={1.2} fill="none" strokeLinecap="round">
          <path d="M100 74 L100 196" />
          <path d="M76 100 Q84 112 90 104" />
          <path d="M124 100 Q116 112 110 104" />
        </g>
      ) : (
        // Ön: göğüs hattı + göbek
        <g stroke={stroke} strokeWidth={1.2} fill="none" strokeLinecap="round">
          <path d="M78 112 Q88 120 98 112" />
          <path d="M102 112 Q112 120 122 112" />
          <circle cx={100} cy={170} r={2} fill={stroke} />
        </g>
      )}
    </g>
  );
}

/**
 * Vücut haritası — ön/arka figür yan yana; tıklanan yere numaralı nokta
 * eklenir, noktaya tıklanınca kaldırılır. `readOnly` (ya da `onChange` yok)
 * iken yalnız gösterir. Numara, `value` dizisindeki sıradır (iki figürde ortak).
 *
 * @param {Array<{view: "front"|"back", x: number, y: number, label?: string}>} value
 * @param {(next: Array) => void} [onChange]
 * @param {boolean} [readOnly]
 * @param {number} [figureWidth=150] - tek figürün en büyük genişliği (px)
 * @param {number} [maxPoints=60]
 */
export default function BodyMap({ value = [], onChange, readOnly, figureWidth = 150, maxPoints = 60 }) {
  const { palette } = useClinicTheme();
  const tone = palette?.primary || COLORS.slate700;
  const editable = !readOnly && typeof onChange === "function";
  const points = Array.isArray(value) ? value : [];

  const handleAdd = (view, e) => {
    if (!editable || points.length >= maxPoints) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    onChange([...points, { view, x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000, label: "" }]);
  };

  const handleRemove = (index, e) => {
    e.stopPropagation();
    if (!editable) return;
    onChange(points.filter((_, i) => i !== index));
  };

  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, justifyItems: "center" }}
      role="group"
      aria-label="Vücut haritası"
    >
      {BODY_VIEWS.map((v) => (
        <Box key={v.value} sx={{ width: "100%", maxWidth: figureWidth, textAlign: "center" }}>
          <Box
            component="svg"
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            onClick={(e) => handleAdd(v.value, e)}
            aria-label={`${v.label} görünüm${editable ? " — nokta eklemek için tıklayın" : ""}`}
            sx={{
              display: "block",
              width: "100%",
              height: "auto",
              cursor: editable ? "crosshair" : "default",
              userSelect: "none",
            }}
          >
            <Silhouette view={v.value} fill={COLORS.slate100} stroke={COLORS.slate300} />
            {points.map((p, i) =>
              p.view !== v.value ? null : (
                <g
                  key={`${p.view}-${i}`}
                  transform={`translate(${p.x * VB_W},${p.y * VB_H})`}
                  onClick={(e) => handleRemove(i, e)}
                  style={{ cursor: editable ? "pointer" : "default" }}
                >
                  <title>{`${i + 1}${p.label ? ` · ${p.label}` : ""}${editable ? " — kaldırmak için tıklayın" : ""}`}</title>
                  <circle r={11} fill={tone} stroke={COLORS.white} strokeWidth={2} />
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={11}
                    fontWeight={600}
                    fill={COLORS.white}
                    style={{ pointerEvents: "none" }}
                  >
                    {i + 1}
                  </text>
                </g>
              )
            )}
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: TYPO.label, color: COLORS.slate500 }}>{v.label}</Typography>
        </Box>
      ))}
      {editable ? (
        <Typography
          sx={{
            gridColumn: "1 / -1",
            fontSize: TYPO.caption,
            color: COLORS.slate500,
            textAlign: "center",
            px: 1,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: alpha(tone, 0.05),
          }}
        >
          Uygulama noktasını eklemek için figüre, kaldırmak için numaraya tıklayın.
        </Typography>
      ) : null}
    </Box>
  );
}
